/**
 * Copyright (c) samjay.dev.
 *
 * Notes:
 * This source code is the private property of samjay.dev.
 * You cannot modify, share or copy these source codes without any formal authorization,
 * otherwise samjay.dev reserves the right to pursue any legal action.
 *
 * Developer: Sam Tsang (info@samjay.dev)
 */

import { Menu, Nav } from 'Nav';
import Bottom from 'Nav/Bottom';
import Dialog from 'components/Dialog';
import { getScroll, setScroll } from 'history';
import moment from 'moment';
import React, { JSX, useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { NavigateFunction, Outlet, Params, useLocation, useNavigate, useParams } from 'react-router-dom';
import Scrollbar from 'react-smooth-scrollbar';
import AppStyle from 'scss/main/App.module.scss';
import SmoothScrollbar from 'smooth-scrollbar';
import { Action } from 'store/slices';
import { RootState } from 'store/types';
import { setHTMLScrollingPosition, setScrollingPosition } from 'utils/scrolling';

const setNavNumber = (name: string) => {
  switch (name) {
    case "/":
      return 0
    case "/blog":
      return 1
    case "/skills":
      return 2
    case "/projects":
      return 3
    case "/contact":
      return 4
  }
}

const setNavName = (name: string) => {
  switch (name) {
    case "/":
      return "Home"
    case "/blog":
      return "Blog"
    case "/skills":
      return "Skills"
    case "/projects":
      return "Projects"
    case "/skills/mobile":
      return 'Skills'
    case "/skills/web":
      return 'Skills'
    case "/skills/cloud":
      return 'Skills'
    case "/contact":
      return "Contact"
    default:
      return ''
  }
}

const setTitle = (name: string) => {
  switch (name) {
    case "/":
      return "Home"
    case "/blog":
      return "Blog"
    case "/skills":
      return "Skills"
    case "/contact":
      return "Contact"
    case "/projects":
      return "Projects"
    case "/skills/mobile":
      return 'Mobile'
    case "/skills/web":
      return 'Web'
    case "/skills/cloud":
      return 'Cloud'
    default:
      return 'Page Not Found'
  }
}

const MainContainer: React.FC<{
  children?: JSX.Element,
  toggleNav: boolean,
  toggle: () => void,
  setScrolling: (value: number) => void,
  setBackStyle: (backStyle: boolean, title?: string) => void,
  router: {
    location: Location;
    navigate: NavigateFunction;
    params: Readonly<Params<string>>;
  }
}> = ({ children, router }) => {
  
  const dispatch = useDispatch();
  const toggleNav = useSelector((state: RootState) => state.ui.toggle!);

  const iOSRef = React.useRef<HTMLInputElement>(null)
  const isNonChromeOrIOS = (navigator.maxTouchPoints > 1 && !/Windows/.test(navigator.userAgent)) || /iPad|iPhone|iPod|Mobile/.test(navigator.userAgent) || !/Chrome/.test(navigator.userAgent)

  const [currentPageName, setCurrentPageName] = useState("");
  const [currentPageNum, setCurrentPageNum] = useState(0);

  const toggle = () => {
    dispatch(Action.toggleNav({}))
  }

  const setScrolling = (value: number) => {
    dispatch(Action.setScrolling({value}))
  }

  const setBackStyle = (backStyle: boolean, title?: string) => {
    dispatch(Action.setBackStyle({backStyle, title: title ?? ''}))
  }

  const goToHome = () => {
    setCurrentPageName('Home');
    setCurrentPageNum(0)
    router.navigate('/')
  }

  const handleScrollData = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    setScrolling(Math.floor(Math.random() * 100))
    setHTMLScrollingPosition(e)
  }

  const elementScrollData = (e: SmoothScrollbar) => {
    setScrolling(Math.floor(Math.random() * 100))
    setScrollingPosition(e)
  }

  useEffect(() => {
    const pathname = router.location.pathname
    setCurrentPageName(setNavName(pathname)!);
    setCurrentPageNum(setNavNumber(pathname)!);

    console.log(`© ${moment().year()} samjay.dev Build 1694074957`)

    document.title = pathname !== '/' ? ((setTitle(pathname)) + ' — Sam Tsang') : 'Sam Tsang — Full Stack Engineer'

    return () => {}
  }, [])

  useEffect(() => {
    const pathname = router.location.pathname
    
    setCurrentPageName(setNavName(pathname)!);
    setCurrentPageNum(setNavNumber(pathname)!)

    if (pathname !== '/skill/mobile' && pathname !== '/skill/web' && pathname !== '/skill/cloud' && !pathname.includes('/blog/')) {
      setBackStyle(false)
    } else {
      setBackStyle(true)
    }

    if (!isNonChromeOrIOS) {
      getScroll()?.scrollTo(0, 0)
    } else {
      window?.scrollTo(0, 0);
    }

    document.title = router.location.pathname !== '/' ? ((setTitle(router.location.pathname)) + ' — Sam Tsang') : 'Sam Tsang — Full Stack Engineer'
  }, [router])

  const handleiOS = (child: JSX.Element) => {

    if (isNonChromeOrIOS) {
      return <div ref={iOSRef} onScroll={(e) => handleScrollData(e)} className={`${AppStyle['App']} ${AppStyle['App-isIOS']}`}>
        {child}
      </div>
    }

    const SBar = Scrollbar as any;

    return <SBar
      className={`${AppStyle['App']} ${AppStyle['App-nonIOS']}`}
      onScroll={(_: any, scrollbarInstance: any) => elementScrollData(scrollbarInstance)}
      damping={0.05}
      continuousScrolling={false}
      alwaysShowTracks={false}
      ref={setScroll}
    >
      {child}
    </SBar>
  }

  return (
    <>
      <Nav
        router={router}
        toggleNav={toggleNav}
        currentPageName={currentPageName}
        goToHome={goToHome}
        currentPageNum={currentPageNum} />
      <Menu currentPageNum={currentPageNum} toggle={toggle} />
      <Dialog />
      {handleiOS(<div className={AppStyle['App-container']} >
          <div className={AppStyle["App-container-content"]}>
            {
              children ? 
                children
              : <>
                <Outlet />
                {currentPageNum !== 4 && <Bottom />}
              </>
            }
          </div>
        </div>)}
    </>
  )
}

function withRouter(Component: any) {
  function ComponentWithRouterProp(props: any) {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    return (
      <Component
        {...props}
        router={{ location, navigate, params }}
      />
    );
  }

  return ComponentWithRouterProp;
}

export default withRouter(MainContainer)